import type { Cluster, EntityPin, MultiPin } from "@mapmycustomers/shared/types/map";

export type PinHit =
  | { source: Cluster; type: "cluster" }
  | { source: EntityPin; type: "entity" }
  | { source: MultiPin; type: "multiPin" };

export interface PinHitTester {
  hit: (latLng: google.maps.LatLng) => PinHit | undefined;
  nearest: (latLng: google.maps.LatLng, maxDistancePx: number) => EntityPin | MultiPin | undefined;
}

const testers = new WeakMap<google.maps.Map, PinHitTester>();

// One hit per physical DOM event, so every listener sees the same answer.
const hitCache = new WeakMap<object, { hit: PinHit | undefined }>();

export const registerPinHitTester = (map: google.maps.Map, tester: PinHitTester) => {
  testers.set(map, tester);
  return () => {
    if (testers.get(map) === tester) {
      testers.delete(map);
    }
  };
};

export const getPinHit = (
  map: google.maps.Map,
  event: google.maps.MapMouseEvent | undefined
): PinHit | undefined => {
  if (!event) {
    return undefined;
  }
  const key = (event.domEvent ?? event) as object;
  const cached = hitCache.get(key);
  if (cached) {
    return cached.hit;
  }
  const hit = event.latLng ? testers.get(map)?.hit(event.latLng) : undefined;
  hitCache.set(key, { hit });
  return hit;
};

export const isPinEvent = (map: google.maps.Map, event: google.maps.MapMouseEvent | undefined) =>
  !!getPinHit(map, event);

export const findNearestPin = (
  map: google.maps.Map,
  latLng: google.maps.LatLng,
  maxDistancePx: number
) => testers.get(map)?.nearest(latLng, maxDistancePx);
